import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import CreatorNavbar from '../components/dashboard/CreatorNavbar';
import CreatorPageHeader from '../components/dashboard/CreatorPageHeader';

const LiveCampaignsPage = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [message, setMessage] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [bidData, setBidData] = useState({ reward: '' });
  const [showBidModal, setShowBidModal] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'creator') return;
    
    const loadCampaigns = async () => {
      setLoading(true);
      const maxRetries = 3;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          setRetryCount(attempt - 1);
          
          const response = await api.get(`/creators/${user.userId}/live-campaigns`);

          // Ensure response.data is an array before setting it
          setCampaigns(Array.isArray(response.data) ? response.data : []);
          setLoading(false);
          return; // Exit retry loop on success
          
        } catch (error) {

          if (attempt === maxRetries) {
            // Final attempt failed
            setMessage('Failed to load campaigns. Please refresh the page.');
            setCampaigns([]); // Set empty array on error
            setLoading(false);
          } else {
            // Wait before retrying (exponential backoff)
            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
    };

    loadCampaigns();
  }, [user]);

  const fetchLiveCampaigns = async () => {
    try {
      const response = await api.get(`/creators/${user.userId}/live-campaigns`);
      // Ensure response.data is an array before setting it
      setCampaigns(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setMessage('Failed to load campaigns');
      setCampaigns([]); // Set empty array on error
    }
  };

  const openBidModal = (campaign) => {
    setSelectedCampaign(campaign);
    setBidData({ reward: '' });
    setShowBidModal(true);
  };

  const closeBidModal = () => {
    setShowBidModal(false);
    setSelectedCampaign(null);
    setBidData({ reward: '' });
  };

  const handleBidSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCampaign || !bidData.reward.trim()) {
      setMessage('Please enter a valid reward amount');
      return;
    }

    try {
      await api.post(`/creators/${user.userId}/campaigns/${selectedCampaign.id}/bid`, bidData);

      setMessage('Bid placed successfully!');
      closeBidModal();
      fetchLiveCampaigns(); // Refresh campaigns to update bid status
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to place bid';
      setMessage(errorMsg);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const handleCompleteProject = async (campaignId) => {
    try {
      // Find the campaign to get bidId
      const campaign = campaigns.find(c => c.id === campaignId);
      if (!campaign || !campaign.bidId) {
        setMessage('Campaign information not found');
        return;
      }

      const confirmed = window.confirm('Are you sure you want to mark this project as completed?');
      if (!confirmed) return;

      await api.post(`/creators/${user.userId}/bids/${campaign.bidId}/complete`);
      setMessage('Project marked as completed successfully!');

      // Refresh campaigns to update status
      fetchLiveCampaigns();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to complete project';
      setMessage(errorMsg);
    }
  };

  if (loading) {
    return (
      <>
        <CreatorNavbar />
        <div className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">
              {retryCount > 0
                ? `Retrying connection... (attempt ${retryCount + 1}/3)`
                : 'Loading live campaigns...'
              }
            </p>
            {retryCount > 0 && (
              <p className="text-sm text-blue-600 mt-2">
                ⚡ Using NgRok tunnel - this may take a moment
              </p>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Live Campaigns - creator Dashboard | CollabOrb</title>
        <meta name="description" content="Browse and bid on live campaigns with active bidding periods" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <CreatorNavbar />

      <div className="min-h-screen bg-gray-50 py-8 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CreatorPageHeader
            kicker="Campaign discovery"
            title="Live Campaigns"
            description="Browse and bid on active campaigns with open bidding windows."
            className="mb-6 sm:mb-8"
            actions={
              <button
                onClick={() => {
                  setMessage('');
                  fetchLiveCampaigns();
                }}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm shadow-md self-start sm:self-auto"
                title="Refresh to see latest bid status updates"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            }
          />

          {/* Message Display */}
          {message && (
            <div className={`mb-6 p-4 rounded-md ${
              message.includes('success') ? 'bg-green-50 text-green-700 border border-green-200' : 
              'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message}
              <button 
                onClick={() => setMessage('')}
                className="ml-2 text-sm underline hover:no-underline"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Location-based Filtering Info */}
          <div className="mb-6 p-4 rounded-md bg-blue-50 border border-blue-200">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-blue-800">
                  <strong>Personalized Campaigns:</strong> You're seeing campaigns that match your location and interests.
                  Brands can target campaigns to specific states, cities, or interest categories.
                  <a href={`/dashboard/creator/profile-manage/${user?.userId}`} className="ml-1 underline hover:no-underline font-medium">
                    Update your profile
                  </a> to see more relevant opportunities.
                </p>
              </div>
            </div>
          </div>

          {/* Campaigns Grid */}
          {campaigns.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-2l-7 7-7-7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Live Campaigns Available</h3>
              <p className="text-gray-600 mb-4">
                There are no active campaigns matching your profile at the moment.
              </p>
              <p className="text-sm text-gray-500">
                Campaigns are filtered based on your location and interests. Brands can target campaigns to specific states, cities, or interest categories.
                {' '}
                <a href={`/dashboard/creator/profile-manage/${user?.userId}`} className="text-blue-600 hover:text-blue-800 underline">
                  Update your profile
                </a>
                {' '}to ensure you see all relevant opportunities.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {(campaigns && Array.isArray(campaigns)) ? campaigns.map((campaign) => (
                <div key={campaign.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                  {/* Campaign Header */}
                  <div className="p-4 sm:p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{campaign.title}</h3>
                        <div className="flex items-center mb-2">
                          {campaign.brandLogoUrl && (
                            <img 
                              src={campaign.brandLogoUrl} 
                              alt={campaign.brandName}
                              className="w-6 h-6 sm:w-8 sm:h-8 rounded-full mr-2 flex-shrink-0"
                            />
                          )}
                          <span className="text-xs sm:text-sm text-gray-600 truncate">{campaign.brandName}</span>
                        </div>
                      </div>
                      <div className={`px-2 py-1 text-xs font-semibold rounded-full ml-2 flex-shrink-0 ${
                        campaign.alreadyBid ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {campaign.alreadyBid ? 'Bid Placed' : 'Open'}
                      </div>
                    </div>

                    {/* Campaign Details */}
                    <div className="space-y-2 mb-4">
                      <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                        <div>
                          <span className="text-gray-600">Budget:</span>
                          <div className="font-medium text-green-600">₹{campaign.budget?.toLocaleString('en-IN')}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Deadline:</span>
                          <div className="font-medium">{formatDate(campaign.deadline)}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Bids:</span>
                          <div className="font-medium">{campaign.numBids}</div>
                        </div>
                      </div>
                      
                      <div className="text-xs sm:text-sm">
                        <span className="text-gray-600">Bidding Ends: </span>
                        <span className="font-medium text-red-600">
                          {formatDate(campaign.biddingEndDate)} 
                          <span className="block sm:inline sm:ml-1">
                            ({getDaysRemaining(campaign.biddingEndDate)} days left)
                          </span>
                        </span>
                      </div>
                    </div>

                    {/* Description */}
                    {campaign.description && (
                      <p className="text-xs sm:text-sm text-gray-600 mb-4 line-clamp-2">
                        {campaign.description}
                      </p>
                    )}

                    {/* Bid Status or Action */}
                    {campaign.alreadyBid ? (
                      <div className="bg-blue-50 p-3 rounded-md">
                        <div className="text-xs sm:text-sm text-blue-800">
                          <strong>Your Bid:</strong> {campaign.bidReward}
                        </div>
                        <div className="text-xs text-blue-600 mt-1">
                          Status: {campaign.bidStatus}
                        </div>
                        {campaign.bidStatus === 'AWARDED' && (
                          <button
                            onClick={() => handleCompleteProject(campaign.id)}
                            className="mt-2 w-full bg-green-600 hover:bg-green-700 text-white py-1.5 px-3 rounded-md transition-colors text-xs font-medium"
                          >
                            Mark as Completed
                          </button>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => openBidModal(campaign)}
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        Place Bid
                      </button>
                    )}
                  </div>
                </div>
              )) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-600">No campaigns data available.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bid Modal */}
      {showBidModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white p-4 sm:p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Place Bid
                </h3>
                <button
                  onClick={closeBidModal}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1 truncate">"{selectedCampaign.title}"</p>
            </div>

            <div className="p-4 sm:p-6">
              {/* Form Section */}
              <form onSubmit={handleBidSubmit}>
                <div className="mb-4">
                  <label htmlFor="reward" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Bid Amount (₹)
                  </label>
                  <input
                    type="text"
                    id="reward"
                    value={bidData.reward}
                    onChange={(e) => setBidData({ ...bidData, reward: e.target.value })}
                    placeholder="Enter your bid amount in rupees..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Campaign Budget: ₹{selectedCampaign.budget?.toLocaleString('en-IN')}
                  </p>
                </div>

                {/* Modal Actions */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={closeBidModal}
                    className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors order-2 sm:order-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="w-full sm:flex-1 px-4 py-2 rounded-md transition-colors order-1 sm:order-2 bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Place Bid
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LiveCampaignsPage;
